import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FUEL_LABELS,
  FUEL_TYPES,
  VEHICLE_TYPE_LABELS,
  VEHICLE_TYPES,
} from "@garage/shared";
import { ApiError } from "../api/client";
import { useSaveVehicle, useVehicle } from "../api/vehicles";
import { Alert, Button, Card, Field, inputClass, Spinner } from "../components/ui";

type FormState = Record<string, string>;

const EMPTY: FormState = {
  regNumber: "",
  nickname: "",
  type: "CAR",
  make: "",
  model: "",
  variant: "",
  fuel: "",
  year: "",
  purchaseDate: "",
  odometerKm: "",
  insurer: "",
  policyNo: "",
  insuranceExpiry: "",
  pucExpiry: "",
  fitnessExpiry: "",
};

export default function VehicleFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const existing = useVehicle(id ?? "");
  const save = useSaveVehicle(id);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!editing || !existing.data) return;
    const v = existing.data.vehicle;
    setForm({
      ...EMPTY,
      ...Object.fromEntries(
        Object.keys(EMPTY).map((k) => [
          k,
          (v as unknown as Record<string, unknown>)[k]?.toString() ?? "",
        ]),
      ),
    });
  }, [editing, existing.data]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFields({});

    // Empty strings mean "not provided", not "clear it" — the API treats an
    // absent key as untouched.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== ""),
    );

    try {
      const { vehicle } = await save.mutateAsync(payload);
      navigate(`/vehicles/${vehicle.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields ?? {});
        if (!err.fields) setError(err.message);
      } else {
        setError("Could not reach the server");
      }
    }
  }

  if (editing && existing.isPending) return <Spinner label="Loading vehicle" />;

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight">
        {editing ? "Edit vehicle" : "Add vehicle"}
      </h1>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-5">
        {error && <Alert>{error}</Alert>}

        <Card className="flex flex-col gap-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Registration number"
              error={fields.regNumber}
              hint="Spaces and dashes are fine"
            >
              <input
                className={`${inputClass} font-mono uppercase`}
                value={form.regNumber}
                onChange={set("regNumber")}
                placeholder="KL 07 AB 1234"
                required
              />
            </Field>

            <Field label="Nickname" hint="What you actually call it">
              <input
                className={inputClass}
                value={form.nickname}
                onChange={set("nickname")}
                placeholder="The Swift"
              />
            </Field>

            <Field label="Type" error={fields.type}>
              <select className={inputClass} value={form.type} onChange={set("type")}>
                {VEHICLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {VEHICLE_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Fuel">
              <select className={inputClass} value={form.fuel} onChange={set("fuel")}>
                <option value="">Not set</option>
                {FUEL_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {FUEL_LABELS[f]}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Make">
              <input className={inputClass} value={form.make} onChange={set("make")} placeholder="Maruti Suzuki" />
            </Field>
            <Field label="Model">
              <input className={inputClass} value={form.model} onChange={set("model")} placeholder="Swift" />
            </Field>
            <Field label="Variant">
              <input className={inputClass} value={form.variant} onChange={set("variant")} placeholder="VXi" />
            </Field>
            <Field label="Year" error={fields.year}>
              <input className={inputClass} type="number" value={form.year} onChange={set("year")} placeholder="2021" />
            </Field>
            <Field label="Purchase date">
              <input className={inputClass} type="date" value={form.purchaseDate} onChange={set("purchaseDate")} />
            </Field>
            <Field label="Current odometer" error={fields.odometerKm} hint="Updates itself as you log services">
              <input className={inputClass} type="number" value={form.odometerKm} onChange={set("odometerKm")} placeholder="42000" />
            </Field>
          </div>
        </Card>

        <Card className="flex flex-col gap-4 p-5">
          <div>
            <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
              Documents
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              You get a warning pill in the garage when any of these is within
              30 days.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Insurer">
              <input className={inputClass} value={form.insurer} onChange={set("insurer")} />
            </Field>
            <Field label="Policy number">
              <input className={inputClass} value={form.policyNo} onChange={set("policyNo")} />
            </Field>
            <Field label="Insurance expiry">
              <input className={inputClass} type="date" value={form.insuranceExpiry} onChange={set("insuranceExpiry")} />
            </Field>
            <Field label="PUC expiry">
              <input className={inputClass} type="date" value={form.pucExpiry} onChange={set("pucExpiry")} />
            </Field>
            <Field label="Fitness expiry" hint="Commercial vehicles">
              <input className={inputClass} type="date" value={form.fitnessExpiry} onChange={set("fitnessExpiry")} />
            </Field>
          </div>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : editing ? "Save changes" : "Add vehicle"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
